import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { IStatus, IMasterStatus } from "./status.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";

import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

// Action Types
export const FETCH_STATUS_LIST_PROGRESS = "FETCH_STATUS_LIST_PROGRESS";
export const FETCH_STATUS_LIST_SUCCESS = "FETCH_STATUS_LIST_SUCCESS";
export const FETCH_STATUS_LIST_FAILED = "FETCH_STATUS_LIST_FAILED";

export const FETCH_STATUS_PROGRESS = "FETCH_STATUS_PROGRESS";
export const FETCH_STATUS_SUCCESS = "FETCH_STATUS_SUCCESS";
export const FETCH_STATUS_FAILED = "FETCH_STATUS_FAILED";

// Action Creators
export const fetchStatusListProgress = () => action(FETCH_STATUS_LIST_PROGRESS);
export const fetchStatusListSuccess = (data: IStatus[]) =>
  action(FETCH_STATUS_LIST_SUCCESS, { data });
export const fetchStatusListFailed = () => action(FETCH_STATUS_LIST_FAILED);

export const fetchStatusProgress = () => action(FETCH_STATUS_PROGRESS);
export const fetchStatusSuccess = (data: IStatus) =>
  action(FETCH_STATUS_SUCCESS, { data });
export const fetchStatusFailed = () => action(FETCH_STATUS_FAILED);

// Async Actions
export const fetchStatusListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date?: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = "";

      if (date) {
        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }

      dispatch(fetchStatusListProgress());
      const res = await api.get(
        `/leads/get-status?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}` +
          searchQuery
      );
      const data: IStatus[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchStatusListSuccess(data));
    } catch (err: any) {
      dispatch(fetchStatusListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const fetchStatusAsync =
  (statusUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchStatusProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(`/leads/get-status?status_uuid=${statusUuid}`);
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchStatusSuccess(data[0]));
      } else {
        dispatch(fetchStatusFailed());
      }
    } catch (err: any) {
      dispatch(fetchStatusFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertStatusAsync =
  (
    status: IStatus,
    onCallback: (isSuccess?: boolean, data?: IStatus) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const {
        created_by_uuid,
        created_by_name,
        modified_by_uuid,
        modified_by_name,
        ...rest
      } = status;
      const res = await api.post("/leads/upsert-status", {
        ...rest,
      });
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_MASTER_STATUS_LIST_PROGRESS =
  "FETCH_MASTER_STATUS_LIST_PROGRESS";
export const FETCH_MASTER_STATUS_LIST_SUCCESS =
  "FETCH_MASTER_STATUS_LIST_SUCCESS";
export const FETCH_MASTER_STATUS_LIST_FAILED =
  "FETCH_MASTER_STATUS_LIST_FAILED";

export const FETCH_MASTER_STATUS_PROGRESS = "FETCH_MASTER_STATUS_PROGRESS";
export const FETCH_MASTER_STATUS_SUCCESS = "FETCH_MASTER_STATUS_SUCCESS";
export const FETCH_MASTER_STATUS_FAILED = "FETCH_MASTER_STATUS_FAILED";

// Action Creators
export const fetchMasterStatusListProgress = () =>
  action(FETCH_MASTER_STATUS_LIST_PROGRESS);
export const fetchMasterStatusListSuccess = (data: IMasterStatus[]) =>
  action(FETCH_MASTER_STATUS_LIST_SUCCESS, { data });
export const fetchMasterStatusListFailed = () =>
  action(FETCH_MASTER_STATUS_LIST_FAILED);

export const fetchMasterStatusProgress = () =>
  action(FETCH_MASTER_STATUS_PROGRESS);
export const fetchMasterStatusSuccess = (data: IMasterStatus) =>
  action(FETCH_MASTER_STATUS_SUCCESS, { data });
export const fetchMasterStatusFailed = () => action(FETCH_MASTER_STATUS_FAILED);

// Async Actions
export const fetchMasterStatusListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date?: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = "";

      if (date) {
        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }

      dispatch(fetchMasterStatusListProgress());
      const res = await api.get(
        `/siteConfiguration/get-master-status?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}` +
          searchQuery
      );
      const data: IMasterStatus[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchMasterStatusListSuccess(data));
    } catch (err: any) {
      dispatch(fetchMasterStatusListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const fetchMasterStatusAsync =
  (statusUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMasterStatusProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/siteConfiguration/get-master-status?status_uuid=${statusUuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchMasterStatusSuccess(data[0]));
      } else {
        dispatch(fetchMasterStatusFailed());
      }
    } catch (err: any) {
      dispatch(fetchMasterStatusFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertMasterStatusAsync =
  (
    status: IMasterStatus,
    onCallback: (isSuccess?: boolean, data?: IMasterStatus) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const {
        created_by_uuid,
        created_by_name,
        modified_by_uuid,
        modified_by_name,
        ...rest
      } = status;
      const res = await api.post("/siteConfiguration/upsert-master-status", {
        ...rest,
      });
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_STATUS_STATE = "CLEAR_STATUS_STATE";
export const CLEAR_STATUS = "CLEAR_STATUS";
export const CLEAR_MASTER_STATUS_STATE = "CLEAR_MASTER_STATUS_STATE";
export const CLEAR_MASTER_STATUS = "CLEAR_MASTER_STATUS";

export const clearStatusState = () => action(CLEAR_STATUS_STATE);
export const clearStatus = () => action(CLEAR_STATUS);
export const clearMasterStatusState = () => action(CLEAR_MASTER_STATUS_STATE);
export const clearMasterStatus = () => action(CLEAR_MASTER_STATUS);
